import React, { createContext, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, useLocation } from 'react-router-dom';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { LicenseInfo } from '@mui/x-data-grid-pro';
import domTargets from 'config/dom/domTargets';
import { Provider } from 'react-redux';
import reportWebVitals from './reportWebVitals';
import store from './store';
import './i18n';
import { createThemeByCompanyStyles } from 'theme';
import App from './App';
import organizationService from './store/organization/service';
import './index.scss';
import * as analytics from 'integrations/analytics';
import { defaultOrganizationSettings } from './common/organization/constants';
import { localStorageWrapper } from './functions/localStorage';
import { getOrganizationAlias } from './common/organization/utils/get-organization-alias';
import { X_DATA_GRID_PRO } from './config/environment/constants';
import config from './config/environment';
import * as Sentry from '@sentry/react';
import { FeatureFlagProvider } from './contexts/featureFlag/FeatureFlagProvider';

LicenseInfo.setLicenseKey(X_DATA_GRID_PRO);

const cachedOrganizationSettings =
  localStorageWrapper.getOrganization() ?? defaultOrganizationSettings;
export const CompanyContext = createContext(cachedOrganizationSettings);

const organizationAlias = getOrganizationAlias();
analytics.loadSegment();

const AppWrapper = () => {
  const location = useLocation();
  const { pathname, search } = location;

  useEffect(() => {
    analytics.page();
  }, [pathname, search]);

  const { data: organizationSettings } =
    organizationService.endpoints.getOrganizationSettings.useQuery(
      organizationAlias,
      {
        skip: !organizationAlias,
      }
    );

  const theme = createThemeByCompanyStyles(
    organizationSettings?.styles || cachedOrganizationSettings.styles
  );

  return (
    <>
      {organizationSettings || cachedOrganizationSettings ? (
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <CompanyContext.Provider
            value={organizationSettings || cachedOrganizationSettings}
          >
            <FeatureFlagProvider>
              <App />
            </FeatureFlagProvider>
          </CompanyContext.Provider>
        </ThemeProvider>
      ) : (
        <div className="errorLoadApp">Error load site</div>
      )}
    </>
  );
};

if (config.SENTRY_DSN) {
  Sentry.init({
    dsn: config.SENTRY_DSN,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      new Sentry.BrowserTracing(),
      new Sentry.Replay({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: 1.0,
  });
}

// create the root container
const container = domTargets.root;
const root = createRoot(container);

root.render(
  <Provider store={store}>
    <BrowserRouter>
      <AppWrapper />
    </BrowserRouter>
  </Provider>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
